/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { FlowEdge, FlowGraph, FlowNode, FlowNodeKind, FlowProblem, NODE_HEIGHT, NODE_WIDTH, outgoing } from './flow-graph';

/** What is selected on the canvas. */
export type FlowSelection = { type: 'node'; id: string } | { type: 'edge'; id: string } | null;

export const NODE_ICONS: Record<FlowNodeKind, string> = {
  start: 'play_circle',
  end: 'stop_circle',
  approval: 'fact_check',
  task: 'assignment',
  condition: 'alt_route',
  parallel: 'call_split'
};

const GRID = 10;

interface DrawnEdge {
  edge: FlowEdge;
  path: string;
  labelX: number;
  labelY: number;
  label: string;
  tone: 'approve' | 'reject' | 'default' | 'plain';
}

/**
 * The drawing surface of the workflow builder.
 *
 * Nodes are boxes placed on a plane; an edge is drawn from the dot under a box
 * to the top of another. Drag a box to move it, drag from its dot to connect
 * it, click a box or a line to select it. The graph object is edited in place
 * and every change is announced, so the builder can mark the flow unsaved and
 * check it again.
 */
@Component({
  selector: 'app-flow-canvas',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './flow-canvas.component.html',
  styleUrl: './flow-canvas.component.scss'
})
export class FlowCanvasComponent {
  @Input({ required: true }) graph!: FlowGraph;
  @Input() selection: FlowSelection = null;
  @Input() problems: FlowProblem[] = [];
  /** Labels for edges and node subtitles, already translated by the builder. */
  @Input() labels: { approve: string; reject: string; otherwise: string; anyone: string; initiator: string; unassigned: string } = {
    approve: 'Approve',
    reject: 'Reject',
    otherwise: 'Otherwise',
    anyone: 'Anyone in',
    initiator: 'The starter',
    unassigned: '—'
  };

  @Output() selectionChange = new EventEmitter<FlowSelection>();
  @Output() changed = new EventEmitter<void>();

  @ViewChild('plane', { static: true }) planeEl!: ElementRef<HTMLElement>;

  readonly nodeWidth = NODE_WIDTH;
  readonly nodeHeight = NODE_HEIGHT;
  readonly icons = NODE_ICONS;

  /** The edge being drawn from a node's dot, following the pointer. */
  linking: { from: FlowNode; x: number; y: number } | null = null;
  private dragging: { node: FlowNode; dx: number; dy: number; moved: boolean } | null = null;
  private edgeCount = 0;

  get planeWidth(): number {
    return Math.max(1200, ...this.graph.nodes.map((n) => n.x + NODE_WIDTH + 240));
  }

  get planeHeight(): number {
    return Math.max(760, ...this.graph.nodes.map((n) => n.y + NODE_HEIGHT + 200));
  }

  isSelected(type: 'node' | 'edge', id: string): boolean {
    return this.selection?.type === type && this.selection.id === id;
  }

  hasProblem(node: FlowNode): boolean {
    return this.problems.some((p) => p.nodeId === node.id);
  }

  edgeHasProblem(edge: FlowEdge): boolean {
    return this.problems.some((p) => p.edgeId === edge.id);
  }

  subtitle(node: FlowNode): string {
    if (node.kind !== 'approval' && node.kind !== 'task') {
      return '';
    }
    if (node.assigneeType === 'initiator') {
      return this.labels.initiator;
    }
    const who = node.assigneeLabel || node.assignee;
    if (!who) {
      return this.labels.unassigned;
    }
    return node.assigneeType === 'group' ? `${this.labels.anyone} ${who}` : who;
  }

  /** Every edge as a path: down from the source's dot, into the target's top — round the side when it goes back up. */
  drawnEdges(): DrawnEdge[] {
    const byId = new Map(this.graph.nodes.map((n) => [n.id, n]));
    return this.graph.edges
      .map((edge): DrawnEdge | null => {
        const a = byId.get(edge.from);
        const b = byId.get(edge.to);
        if (!a || !b) {
          return null;
        }
        const x1 = a.x + NODE_WIDTH / 2;
        const y1 = a.y + NODE_HEIGHT;
        const x2 = b.x + NODE_WIDTH / 2;
        const y2 = b.y;
        let path: string;
        let labelX: number;
        let labelY: number;
        const sideBySide = Math.abs(a.x - b.x) >= NODE_WIDTH + 20;
        if (y2 >= y1 + 20) {
          const bend = Math.max(30, (y2 - y1) / 2);
          path = `M ${x1} ${y1} C ${x1} ${y1 + bend}, ${x2} ${y2 - bend}, ${x2} ${y2}`;
          labelX = (x1 + x2) / 2;
          labelY = (y1 + y2) / 2;
        } else if (sideBySide) {
          // level with it, or above it in another column: across, from the facing sides
          const rightward = b.x > a.x;
          const sx = rightward ? a.x + NODE_WIDTH : a.x;
          const tx = rightward ? b.x : b.x + NODE_WIDTH;
          const sy = a.y + NODE_HEIGHT / 2;
          const ty = b.y + NODE_HEIGHT / 2;
          const mid = (sx + tx) / 2;
          path = `M ${sx} ${sy} C ${mid} ${sy}, ${mid} ${ty}, ${tx} ${ty}`;
          labelX = mid;
          labelY = (sy + ty) / 2;
        } else {
          // back up the page: out and round the right-hand side of both boxes
          const side = Math.max(a.x, b.x) + NODE_WIDTH + 50;
          path = `M ${x1} ${y1} C ${x1} ${y1 + 50}, ${side} ${y1 + 50}, ${side} ${(y1 + y2) / 2} S ${x2} ${y2 - 50}, ${x2} ${y2}`;
          labelX = side;
          labelY = (y1 + y2) / 2;
        }
        return { edge, path, labelX, labelY, ...this.edgeLabel(edge, a) };
      })
      .filter((e): e is DrawnEdge => e !== null);
  }

  private edgeLabel(edge: FlowEdge, from: FlowNode): { label: string; tone: DrawnEdge['tone'] } {
    if (from.kind === 'approval' && edge.outcome) {
      return edge.outcome === 'approve' ? { label: this.labels.approve, tone: 'approve' } : { label: this.labels.reject, tone: 'reject' };
    }
    if (from.kind === 'condition') {
      if (edge.isDefault) {
        return { label: this.labels.otherwise, tone: 'default' };
      }
      const c = edge.condition;
      const symbol: Record<string, string> = { eq: '=', ne: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', contains: '∋', empty: '= ∅', notEmpty: '≠ ∅' };
      return {
        label: c?.field ? `${c.field} ${symbol[c.op] ?? c.op} ${c.op === 'empty' || c.op === 'notEmpty' ? '' : (c.value ?? '')}`.trim() : '?',
        tone: 'plain'
      };
    }
    return { label: '', tone: 'plain' };
  }

  // ---- pointer --------------------------------------------------------------------

  private point(event: PointerEvent): { x: number; y: number } {
    const box = this.planeEl.nativeElement.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  }

  onNodeDown(event: PointerEvent, node: FlowNode): void {
    if (event.button !== 0) {
      return;
    }
    event.stopPropagation();
    const p = this.point(event);
    this.dragging = { node, dx: p.x - node.x, dy: p.y - node.y, moved: false };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    this.selectionChange.emit({ type: 'node', id: node.id });
  }

  onPortDown(event: PointerEvent, node: FlowNode): void {
    if (event.button !== 0) {
      return;
    }
    event.stopPropagation();
    const p = this.point(event);
    this.linking = { from: node, x: p.x, y: p.y };
  }

  onPointerMove(event: PointerEvent): void {
    const p = this.point(event);
    if (this.dragging) {
      const x = Math.max(0, Math.round((p.x - this.dragging.dx) / GRID) * GRID);
      const y = Math.max(0, Math.round((p.y - this.dragging.dy) / GRID) * GRID);
      if (x !== this.dragging.node.x || y !== this.dragging.node.y) {
        this.dragging.node.x = x;
        this.dragging.node.y = y;
        this.dragging.moved = true;
      }
    } else if (this.linking) {
      this.linking = { ...this.linking, x: p.x, y: p.y };
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (this.dragging) {
      if (this.dragging.moved) {
        this.changed.emit();
      }
      this.dragging = null;
      return;
    }
    if (this.linking) {
      const p = this.point(event);
      const target = this.graph.nodes.find((n) => p.x >= n.x && p.x <= n.x + NODE_WIDTH && p.y >= n.y && p.y <= n.y + NODE_HEIGHT);
      if (target) {
        this.connect(this.linking.from, target);
      }
      this.linking = null;
    }
  }

  onBackgroundDown(): void {
    this.selectionChange.emit(null);
  }

  selectEdge(event: Event, edge: FlowEdge): void {
    event.stopPropagation();
    this.selectionChange.emit({ type: 'edge', id: edge.id });
  }

  /**
   * Draws an edge, giving it the meaning its source suggests: an approval's
   * one line goes either way, and a second one makes them "approve" and
   * "reject"; a condition's first path asks for a condition and its second is
   * "otherwise".
   */
  connect(from: FlowNode, to: FlowNode): FlowEdge | null {
    if (from.id === to.id || to.kind === 'start' || from.kind === 'end') {
      return null;
    }
    if (this.graph.edges.some((e) => e.from === from.id && e.to === to.id)) {
      return null;
    }
    const outs = outgoing(this.graph, from.id);
    const edge: FlowEdge = { id: this.edgeId(), from: from.id, to: to.id };
    if (from.kind === 'approval') {
      if (outs.length === 1 && !outs[0].outcome) {
        // its one line went either way; a second one splits the decision
        outs[0].outcome = 'approve';
        edge.outcome = 'reject';
      } else if (!outs.some((e) => e.outcome === 'approve')) {
        edge.outcome = 'approve';
      } else if (!outs.some((e) => e.outcome === 'reject')) {
        edge.outcome = 'reject';
      }
    } else if (from.kind === 'condition') {
      if (outs.length === 1 && !outs.some((e) => e.isDefault)) {
        edge.isDefault = true;
      } else {
        edge.condition = { field: '', op: 'eq', value: '' };
      }
    }
    this.graph.edges.push(edge);
    this.changed.emit();
    this.selectionChange.emit({ type: 'edge', id: edge.id });
    return edge;
  }

  private edgeId(): string {
    let id: string;
    do {
      id = `e${Date.now().toString(36)}${(this.edgeCount++).toString(36)}`;
    } while (this.graph.edges.some((e) => e.id === id));
    return id;
  }

  /** Arrow keys nudge the selected box. */
  onKey(event: KeyboardEvent): void {
    const sel = this.selection;
    if (sel?.type !== 'node') {
      return;
    }
    const node = this.graph.nodes.find((n) => n.id === sel.id);
    const moves: Record<string, [number, number]> = { ArrowLeft: [-GRID, 0], ArrowRight: [GRID, 0], ArrowUp: [0, -GRID], ArrowDown: [0, GRID] };
    const move = moves[event.key];
    if (!node || !move) {
      return;
    }
    event.preventDefault();
    node.x = Math.max(0, node.x + move[0]);
    node.y = Math.max(0, node.y + move[1]);
    this.changed.emit();
  }

  trackNode(_: number, node: FlowNode): string {
    return node.id;
  }

  trackEdge(_: number, drawn: DrawnEdge): string {
    return drawn.edge.id;
  }
}
